import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSeller } from "../context/SellerContext";
import { useCategories } from "../context/CategoryContext";
import api from "../services/api";
import "./Header.css";

function Header({ cartCount = 0 }) {
  const { user, logout, isAdmin } = useAuth();
  const { shopName, logoUrl, primaryColor, isSellerSite } = useSeller();
  const { categories: ctxCategories } = useCategories();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [sortedCategories, setSortedCategories] = useState([]); // 정렬된 카테고리 상태 추가
  const categoryBtnRef = React.useRef(null);

  useEffect(() => {
    // 카테고리 설정(순서 포함) 로딩
    const fetchCategories = async () => {
      // CategoryContext에서 이미 로드된 데이터 사용
      const SPECIAL_CATEGORIES = [
        { name: '베스트 상품', categoryValue: 'best', value: 'best' },
        { name: '신상품', categoryValue: 'new', value: 'new' },
        { name: '추천 상품', categoryValue: 'recommended', value: 'recommended' }
      ];

      const fixedOrder = ['hotdeal', 'best', 'new', 'recommended'];

      // DB 카테고리를 Header용 형식으로 변환
      const dbCats = ctxCategories.map(cat => ({
        name: cat.name || cat.title,
        value: cat.categoryValue,
        hot: cat.isHot || false,
        description: cat.description,
        displayOrder: cat.displayOrder
      }));

      const specialCats = [
        dbCats.find(c => c.value === 'hotdeal'),
        ...SPECIAL_CATEGORIES
      ].filter(Boolean).map(cat => ({
        ...cat,
        displayOrder: fixedOrder.indexOf(cat.value) - 100
      }));

      const regularCats = dbCats.filter(c => c.value !== 'hotdeal');

      const combined = [...specialCats, ...regularCats];
      combined.sort((a, b) => (a.displayOrder || 999) - (b.displayOrder || 999));

      setSortedCategories(combined);
    };
    if (ctxCategories.length > 0) fetchCategories();
  }, [ctxCategories]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;

      // 스크롤 방향 감지
      if (scrollPosition > lastScrollY && scrollPosition > 100) {
        // 아래로 스크롤 중이고 100px 이상 스크롤했을 때 헤더 숨김
        setIsHidden(true);
        setShowCategoryDropdown(false); // 드롭다운 닫기
      }

      // 최상단(50px 이내)에 도착했을 때만 헤더 표시
      if (scrollPosition < 50) {
        setIsHidden(false);
      }

      setIsScrolled(scrollPosition > 50);
      setLastScrollY(scrollPosition);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleCategoryMouseEnter = () => {
    if (categoryBtnRef.current) {
      const rect = categoryBtnRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom,
        left: rect.left,
      });
    }
    setShowCategoryDropdown(true);
  };

  const handleCategoryMouseLeave = () => {
    setShowCategoryDropdown(false);
  };

  // 전체 카테고리 목록 (Context에서 조회)
  const allCategories = ctxCategories.map(cat => ({ name: cat.name || cat.title, value: cat.categoryValue }));

  return (
    <header
      className={`header ${isScrolled ? "scrolled" : ""} ${
        isHidden ? "header-compact" : ""
      }`}
    >
      {/* 최상단 유틸리티 바 */}
      <div className="utility-bar">
        <div className="utility-container">
          <div className="utility-left">
            <Link to="/">홈</Link>

            {!isSellerSite && (
              <>
                <span className="divider">|</span>
                <Link to="/adminlogin">관리자</Link>
              </>
            )}
          </div>
          <div className="utility-right">
            {user ? (
              <>
                <span className="welcome">{user.name}님</span>
                <span className="divider">|</span>
                <button onClick={handleLogout} className="utility-btn">
                  로그아웃
                </button>
                {isAdmin && !isSellerSite && (
                  <>
                    <span className="divider">|</span>
                    <Link to="/admin/dashboard">관리자페이지</Link>
                  </>
                )}
              </>
            ) : (
              <>
                <Link to="/login">로그인</Link>
                <span className="divider">|</span>
                <Link to="/signup">회원가입</Link>
              </>
            )}
            <span className="divider">|</span>
            <Link to="/inquiry">1:1문의</Link>
            <span className="divider">|</span>
            <Link to="/mypage">마이페이지</Link>
            <span className="divider">|</span>
            <Link to="/orders">주문배송조회</Link>
            <span className="divider">|</span>
            <Link to="/cart">
              장바구니
              {cartCount > 0 && (
                <span className="utility-cart-count">{cartCount}</span>
              )}
            </Link>
          </div>
        </div>
      </div>

      {/* 메인 헤더 */}
      <div
        className="main-header"
        style={primaryColor ? { borderBottomColor: primaryColor } : {}}
      >
        <div className="main-header-container">
          {/* 좌측 로고 */}
          <Link to="/" className="logo-section">
            {logoUrl ? (
              <img src={logoUrl} alt={shopName} className="logo-image" />
            ) : (
              <div className="logo-text-container">
                <h1 className="shop-name">{shopName}</h1>
                <p className="shop-subtitle">당신의 행복한 순간을 함께합니다</p>
              </div>
            )}
          </Link>

          {/* 중앙 검색 */}
          <div className="search-section">
            <form className="search-form" onSubmit={handleSearch}>
              <input
                type="text"
                className="search-input"
                placeholder="최고의 가성비 배송비 꽃마켓!"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button type="submit" className="search-button">
                검색
              </button>
            </form>
          </div>

          <div className="header-info-section">
          </div>
        </div>


      </div>


      {/* 카테고리 네비게이션 */}
      <div className="category-nav">
        <div className="category-nav-container">
          {/* 전체 카테고리 드롭다운 */}
          <div
            className="category-dropdown-wrapper"
            onMouseEnter={handleCategoryMouseEnter}
            onMouseLeave={handleCategoryMouseLeave}
          >
            <button ref={categoryBtnRef} className="category-all-btn">
              ☰ 전체 카테고리 보기
              <span className="dropdown-arrow">
                {showCategoryDropdown ? "▲" : "▼"}
              </span>
            </button>
          </div>

          {/* 동적 카테고리 링크 */}
          {sortedCategories.map((category) => (
             <Link 
               key={category.value}
               to={`/products?category=${category.value}`} 
               className={`category-link ${category.hot ? 'hot' : ''} ${category.value === 'best' ? 'best' : ''}`}
             >
               {category.name}
             </Link>
          ))}
        </div>
      </div>


      {/* 드롭다운 메뉴 - 헤더 외부에 렌더링 */}
      {showCategoryDropdown && (
        <div
          className="category-dropdown-menu-fixed"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
          }}
          onMouseEnter={handleCategoryMouseEnter}
          onMouseLeave={handleCategoryMouseLeave}
        >
          <div className="category-dropdown-grid">
            {sortedCategories.map((category) => (
              <Link
                key={category.value}
                to={`/products?category=${category.value}`}
                className={`category-dropdown-item ${
                  category.hot ? "hot" : ""
                } ${category.value === 'best' ? 'best' : ''}`}
                onClick={() => setShowCategoryDropdown(false)}
              >
                {category.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}

export default Header;
